import { useEffect } from "react";
import { AccessTier } from "@/lib/usage";
import { trackEvent } from "@/lib/analytics";
import { Clock, AlertTriangle, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  tier: AccessTier;
  hoursLeft: number;
  remainingUses: number;
  onSignUp: () => void;
}

export default function UsageBanner({ tier, hoursLeft, remainingUses, onSignUp }: Props) {
  useEffect(() => {
    if (tier === "nudge") trackEvent("signup_prompt_shown", { type: "nudge" });
    if (tier === "locked") trackEvent("signup_prompt_shown", { type: "locked" });
  }, [tier]);

  const handleSignUp = () => {
    trackEvent("signup_clicked", { from: tier });
    onSignUp();
  };

  if (tier === "free" && hoursLeft > 6) {
    // Don't show anything for the first 18hrs — let them enjoy it
    return null;
  }

  if (tier === "free") {
    // Gentle reminder when under 6hrs left
    return (
      <div className="mx-4 mt-3 px-3 py-2.5 rounded-lg bg-primary/5 border border-primary/10 flex items-center gap-2">
        <Clock className="w-4 h-4 text-primary shrink-0" />
        <p className="text-xs text-foreground flex-1">
          <span className="font-medium">{Math.ceil(hoursLeft)}h left</span> on your free access.{" "}
          <button onClick={handleSignUp} className="text-primary hover:underline font-medium">
            Sign up
          </button>{" "}
          to keep using Jobily.
        </p>
      </div>
    );
  }

  if (tier === "nudge") {
    return (
      <div className="mx-4 mt-3 px-3 py-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
        <p className="text-xs text-foreground flex-1">
          <span className="font-medium">Free trial ended.</span>{" "}
          <button onClick={handleSignUp} className="text-primary hover:underline font-medium">
            Sign up
          </button>{" "}
          to continue using Jobily.
        </p>
      </div>
    );
  }

  // Locked
  return (
    <div className="mx-4 mt-3 px-3 py-3 rounded-xl bg-muted border border-border text-center space-y-2">
      <Lock className="w-5 h-5 mx-auto text-muted-foreground" />
      <p className="text-sm font-medium text-foreground">Your free access has ended</p>
      <p className="text-xs text-muted-foreground">
        Sign up to continue using autofill, cover letters, and Q&A.
      </p>
      <Button size="sm" className="w-full mt-1" onClick={handleSignUp}>
        Create Account
      </Button>
    </div>
  );
}

